import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Header.css';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import SchoolIcon from '@mui/icons-material/School';
import { useAuth } from '../contexts/AuthContext';
import { useOrgInstitution } from '../contexts/OrgInstitutionContext';
import useIsMobile from '../hooks/useIsMobile';
import LanguageSwitcher from './LanguageSwitcher';
import GoogleClassroomStatus from './GoogleClassroomStatus';
import NotificationPanel from './NotificationPanel';
import { useNotifications } from '../hooks/useNotifications';

const Header: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { isMobile } = useIsMobile();
  const { user, logout, isTeacher, organization: authOrganization, institutions: authInstitutions, currentInstitution } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const {
    selectedOrganizationId,
    selectedInstitutionId,
    organizations,
    institutions,
    setSelectedOrganizationId,
    setSelectedInstitutionId
  } = useOrgInstitution();
  const { t } = useTranslation();

  const getUserInitials = (): string => {
    if (!user) return 'U';
    const firstInitial = user.first_name?.charAt(0).toUpperCase() || '';
    const lastInitial = user.last_name?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}` || 'U';
  };

  const getUserAvatarColor = (): string => {
    if (!user) return '#1976d2';
    const colors = [
      '#1976d2', // blue
      '#388e3c', // green
      '#d32f2f', // red
      '#f57c00', // orange
      '#7b1fa2', // purple
      '#0097a7', // cyan
      '#c2185b', // pink
      '#5d4037', // brown
      '#455a64', // blue grey
      '#e64a19', // deep orange
    ];
    return colors[user.id % colors.length];
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
  };

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-right">
          {/* Organization/Institution Dropdowns */}
          {user?.user_role === 'global_admin' && (
            <div className="header-dropdowns">
              <select
                className="header-select"
                value={selectedOrganizationId || ''}
                onChange={(e) => setSelectedOrganizationId(e.target.value ? parseInt(e.target.value) : null)}
                aria-label={t('header.selectOrganization', { defaultValue: 'Select organization' })}
              >
                <option value="">{t('header.allOrganizations', 'Organizations')}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              
              <select
                className="header-select"
                value={selectedInstitutionId || ''}
                onChange={(e) => setSelectedInstitutionId(e.target.value ? parseInt(e.target.value) : null)}
                aria-label={t('header.selectInstitution', { defaultValue: 'Select institution' })}
              >
                <option value="">{t('header.allInstitutions', 'All Institutions')}</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {user?.user_role === 'org_admin' && (
            <div className="header-dropdowns">
              <select
                className="header-select"
                value={selectedInstitutionId || ''}
                onChange={(e) => setSelectedInstitutionId(e.target.value ? parseInt(e.target.value) : null)}
                aria-label={t('header.selectInstitution', { defaultValue: 'Select institution' })}
              >
                <option value="">{t('header.allInstitutions', 'All Institutions')}</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Teacher School/Organization Info Display */}
          {isTeacher && (
            <div 
              className={`teacher-context-info ${(authInstitutions.length === 0 || !authOrganization) ? 'warning' : 'normal'}`}
              aria-label={t('header.school')}
            >
              {(authInstitutions.length === 0 || !authOrganization) ? (
                <>
                  <WarningIcon 
                    sx={{ fontSize: 20, color: '#92400e' }} 
                    aria-hidden="true"
                  />
                  <div className="context-text">
                    <span className="primary-text">
                      {t('header.notAssigned')}
                    </span>
                    <span className="secondary-text">
                      {t('header.contactAdminForAssignment')}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <SchoolIcon 
                    sx={{ fontSize: 20, color: '#2d5016' }} 
                    aria-hidden="true"
                  />
                  <div className="context-text">
                    <span className="primary-text">
                      {currentInstitution?.name || authInstitutions[0]?.name || t('header.notAssigned')}
                    </span>
                    <span className="secondary-text">
                      {authOrganization?.name || t('header.notAssigned')}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
          
          <div className="search-container">
            <input 
              type="text" 
              placeholder={t('header.searchPlaceholder')}
              className="search-input"
              aria-label={t('header.searchPlaceholder')}
            />
            <span className="search-icon">
              <SearchIcon sx={{ fontSize: 18, color: '#8a8a8a' }} />
            </span>
          </div>
          
          <div className="notifications">
            <button 
              className="notification-btn"
              onClick={() => setNotifOpen((prev) => !prev)}
              aria-label={t('notifications.bellLabel', { count: unreadCount, defaultValue: `Notifications - ${unreadCount} unread` })}
              aria-expanded={notifOpen}
              aria-haspopup="dialog"
            >
              <span className="notification-icon">
                <NotificationsIcon sx={{ fontSize: 20, color: '#1a1a2e' }} aria-hidden="true" />
              </span>
              {unreadCount > 0 && (
                <span className="notification-badge" aria-hidden="true">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <NotificationPanel
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onDelete={deleteNotification}
                onClose={() => setNotifOpen(false)}
              />
            )}
          </div>
          
          <div className="header-lang-switcher">
            <LanguageSwitcher />
          </div>
          
          <div className="header-gc-status">
            <GoogleClassroomStatus />
          </div>
          
          <div className="user-menu">
            <button 
              className="user-menu-btn"
              onClick={toggleDropdown}
              aria-label={`User menu for ${user?.full_name || 'User'}`}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <div 
                className="user-avatar"
                style={{
                  backgroundColor: getUserAvatarColor(),
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
                aria-hidden="true"
              >
                {getUserInitials()}
              </div>
              <span className="user-name">{user?.full_name || 'User'}</span>
              <span className="dropdown-arrow">
                <ExpandMoreIcon sx={{ fontSize: 16, color: '#8a8a8a' }} aria-hidden="true" />
              </span>
            </button>
            
            {dropdownOpen && (
              <div className="user-dropdown">
                {/* Mobile-only: admin org/institution switcher */}
                {isMobile && user?.user_role === 'global_admin' && (
                  <div className="dropdown-admin-context">
                    <select
                      className="dropdown-context-select"
                      value={selectedOrganizationId || ''}
                      onChange={(e) => setSelectedOrganizationId(e.target.value ? parseInt(e.target.value) : null)}
                      aria-label={t('header.selectOrganization', { defaultValue: 'Select organization' })}
                    >
                      <option value="">{t('header.allOrganizations', 'All Organizations')}</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <select
                      className="dropdown-context-select"
                      value={selectedInstitutionId || ''}
                      onChange={(e) => setSelectedInstitutionId(e.target.value ? parseInt(e.target.value) : null)}
                      aria-label={t('header.selectInstitution', { defaultValue: 'Select institution' })}
                    >
                      <option value="">{t('header.allInstitutions', 'All Institutions')}</option>
                      {institutions.map((inst) => (
                        <option key={inst.id} value={inst.id}>{inst.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isMobile && user?.user_role === 'org_admin' && (
                  <div className="dropdown-admin-context">
                    <select
                      className="dropdown-context-select"
                      value={selectedInstitutionId || ''}
                      onChange={(e) => setSelectedInstitutionId(e.target.value ? parseInt(e.target.value) : null)}
                      aria-label={t('header.selectInstitution', { defaultValue: 'Select institution' })}
                    >
                      <option value="">{t('header.allInstitutions', 'All Institutions')}</option>
                      {institutions.map((inst) => (
                        <option key={inst.id} value={inst.id}>{inst.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <ul className="dropdown-list" role="menu">
                  <li><button className="dropdown-item" role="menuitem">{t('header.profile')}</button></li>
                  <li><button className="dropdown-item" role="menuitem">{t('header.accountSettings')}</button></li>
                  <li><button className="dropdown-item" role="menuitem">{t('header.help')}</button></li>
                  <li className="dropdown-divider"></li>
                  <li><button className="dropdown-item logout" onClick={handleLogout} role="menuitem">{t('header.signOut')}</button></li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
